import type { OnboardingResponse } from '@accounts/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { STR } from '../../../i18n';
import { ApiError, listAccessCatalog } from '../../../lib/api';
import { onboardUser } from '../../../lib/lifecycleApi';
import { queryKeys } from '../../../lib/queryKeys';
import { Modal, ModalForm } from '../../../ui/Modal';
import { Callout, Tile } from '../../../ui/Primitives';
import {
  checkboxRowStyle,
  checkboxStyle,
  fieldLabelStyle,
  hintStyle,
  modalButtonStyle,
  searchFieldStyle,
  textFieldStyle,
} from '../../../ui/styles';

import {
  buildOnboardingRequest,
  onboardingCounts,
  onboardingGroups,
  onboardingMode,
  type OnboardingPersonDraft,
} from './model';

type Step = 1 | 2 | 3;

const EMPTY_PERSON: OnboardingPersonDraft = {
  name: '',
  email: '',
  title: '',
  isAdmin: false,
};

export function OnboardingDialog({
  hrOnly,
  onCancel,
  onCreated,
}: {
  hrOnly: boolean;
  onCancel: () => void;
  onCreated: (result: OnboardingResponse) => void;
}) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>(1);
  const [person, setPerson] = useState(EMPTY_PERSON);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  const catalogQuery = useQuery({
    queryKey: queryKeys.accessCatalog(false),
    queryFn: () => listAccessCatalog(),
  });
  const items = useMemo(
    () => (catalogQuery.data?.data ?? []).filter((item) => item.retiredAt === null),
    [catalogQuery.data],
  );
  const visibleItems = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (needle === '') return items;
    return items.filter((item) =>
      [item.name, item.application?.name ?? '', ...item.path.map((part) => part.name)]
        .join(' ')
        .toLowerCase()
        .includes(needle),
    );
  }, [items, search]);
  const groups = onboardingGroups(visibleItems);
  const picked = items.filter((item) => selected.has(item.id));
  const counts = onboardingCounts(items, selected, hrOnly);

  const mutation = useMutation({
    mutationFn: () => onboardUser(buildOnboardingRequest(person, items, selected, hrOnly)),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.users });
      void queryClient.invalidateQueries({ queryKey: queryKeys.accessAll });
      onCreated(result);
    },
    onError: (failure) => {
      setError(failure instanceof ApiError ? failure.message : STR.errors.generic);
    },
  });

  function next() {
    if (step === 1 && (person.name.trim() === '' || person.email.trim() === '')) {
      setError(STR.modals.createUser.required);
      return;
    }
    setError(null);
    setStep((step + 1) as Step);
  }

  function toggle(id: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const footer = (
    <>
      {step === 1 ? null : (
        <button
          type="button"
          className="ghost"
          style={{ ...modalButtonStyle, marginRight: 'auto' }}
          disabled={mutation.isPending}
          onClick={() => {
            setError(null);
            setStep((step - 1) as Step);
          }}
        >
          {STR.access.onboarding.back}
        </button>
      )}
      <button
        type="button"
        className="ghost"
        style={modalButtonStyle}
        disabled={mutation.isPending}
        onClick={onCancel}
      >
        {STR.common.cancel}
      </button>
      {step < 3 ? (
        <button
          type="button"
          className="prim"
          style={modalButtonStyle}
          disabled={step === 2 && (catalogQuery.isLoading || catalogQuery.isError)}
          onClick={next}
        >
          {step === 1
            ? STR.access.onboarding.nextAccess
            : STR.access.onboarding.nextReview}
        </button>
      ) : (
        <button
          type="button"
          className="prim"
          style={modalButtonStyle}
          disabled={mutation.isPending}
          onClick={() => {
            setError(null);
            mutation.mutate();
          }}
        >
          {mutation.isPending
            ? STR.access.onboarding.creating
            : STR.access.onboarding.create}
        </button>
      )}
    </>
  );

  return (
    <Modal
      title={STR.modals.createUser.title}
      lead={hrOnly ? STR.access.onboarding.leadHr : STR.access.onboarding.leadAdmin}
      width={step === 2 ? 'min(720px,100%)' : 'min(580px,100%)'}
      dismissable={!mutation.isPending}
      onClose={onCancel}
      footer={footer}
    >
      <ModalForm>
        <StepRail current={step} />
        {error === null ? null : (
          <Callout tone="danger" role="alert" style={{ fontWeight: 600 }}>
            {error}
          </Callout>
        )}

        {step === 1 ? (
          <>
            <TextField
              label={STR.modals.createUser.nameLabel}
              type="text"
              value={person.name}
              placeholder={STR.modals.createUser.namePlaceholder}
              onChange={(name) => {
                setPerson((current) => ({ ...current, name }));
              }}
            />
            <TextField
              label={STR.modals.createUser.emailLabel}
              type="email"
              value={person.email}
              placeholder={STR.modals.createUser.emailPlaceholder}
              onChange={(email) => {
                setPerson((current) => ({ ...current, email }));
              }}
            />
            <TextField
              label={STR.access.onboarding.jobTitle}
              type="text"
              value={person.title}
              placeholder={STR.access.onboarding.jobTitlePlaceholder}
              onChange={(title) => {
                setPerson((current) => ({ ...current, title }));
              }}
            />
            {hrOnly ? (
              <Callout tone="neutral">{STR.access.onboarding.membersOnly}</Callout>
            ) : (
              <label style={checkboxRowStyle}>
                <input
                  type="checkbox"
                  checked={person.isAdmin}
                  onChange={(event) => {
                    setPerson((current) => ({ ...current, isAdmin: event.target.checked }));
                  }}
                  style={checkboxStyle}
                />
                <span>
                  <span style={{ display: 'block', fontSize: 13, fontWeight: 650 }}>
                    {STR.modals.createUser.adminTitle}
                  </span>
                  <span className="tx-subtle" style={{ ...hintStyle, marginTop: 2, fontSize: 12 }}>
                    {STR.modals.createUser.adminNote}
                  </span>
                </span>
              </label>
            )}
          </>
        ) : null}

        {step === 2 ? (
          <>
            <p className="tx-subtle" style={{ margin: 0, fontSize: 12.5, lineHeight: 1.55 }}>
              {STR.access.onboarding.pickLead(person.name.split(/\s+/)[0] || STR.access.onboarding.they, hrOnly)}
            </p>
            <input
              className="field"
              type="search"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
              }}
              placeholder={STR.access.onboarding.search}
              aria-label={STR.access.onboarding.search}
              style={searchFieldStyle}
            />
            <div
              style={{
                border: '1px solid var(--border)',
                borderRadius: 10,
                maxHeight: 320,
                overflow: 'auto',
              }}
            >
              {catalogQuery.isLoading ? (
                <div className="tx-subtle" style={{ padding: 18, fontSize: 12.5 }}>
                  {STR.common.loading}
                </div>
              ) : catalogQuery.isError ? (
                <div style={{ padding: 14 }}>
                  <Callout tone="danger">{STR.access.onboarding.catalogError}</Callout>
                </div>
              ) : groups.length === 0 ? (
                <div className="tx-subtle" style={{ padding: 18, fontSize: 12.5 }}>
                  {STR.access.onboarding.noMatches}
                </div>
              ) : (
                groups.map((group) => (
                  <div key={group.key}>
                    <div
                      className="tx-subtle"
                      style={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        padding: '8px 12px',
                        borderBottom: '1px solid var(--border)',
                        background: 'var(--surface-2)',
                        fontSize: 10.5,
                        fontWeight: 750,
                        letterSpacing: '.07em',
                        textTransform: 'uppercase',
                      }}
                    >
                      {group.name}
                    </div>
                    {group.items.map((item) => {
                      const mode = onboardingMode(item, hrOnly);
                      return (
                        <label
                          key={item.id}
                          style={{
                            display: 'flex',
                            gap: 10,
                            alignItems: 'center',
                            padding: '9px 12px',
                            borderBottom: '1px solid var(--border)',
                            cursor: 'pointer',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selected.has(item.id)}
                            onChange={() => {
                              toggle(item.id);
                            }}
                            style={checkboxStyle}
                          />
                          <span style={{ minWidth: 0, flex: 1 }}>
                            <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600 }}>
                              {item.name}
                            </span>
                            <span className="tx-subtle" style={{ display: 'block', fontSize: 11.5 }}>
                              {item.application?.name ?? STR.access.onboarding.outsideSystem}
                              {!item.requestable ? ` · ${STR.access.onboarding.hiddenRow}` : ''}
                            </span>
                          </span>
                          <span
                            className={mode === 'direct' ? 'tx-success-strong' : 'tx-subtle'}
                            style={{ flex: 'none', fontSize: 11.5, fontWeight: mode === 'direct' ? 650 : 500 }}
                          >
                            {mode === 'direct'
                              ? STR.access.onboarding.givenAtOnce
                              : STR.access.onboarding.requestToAdmin}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                ))
              )}
            </div>
            <div className="tx-subtle" style={{ fontSize: 12 }}>
              {STR.access.onboarding.pickSummary(
                selected.size,
                counts.givenNow,
                counts.requestsToAdmin,
              )}
            </div>
          </>
        ) : null}

        {step === 3 ? (
          <>
            <div
              style={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 10 }}
            >
              <Tile label={STR.access.onboarding.givenNow}>{counts.givenNow}</Tile>
              <Tile label={STR.access.onboarding.requestsToAdmin}>{counts.requestsToAdmin}</Tile>
              <Tile label={STR.access.onboarding.itSetupItems}>{counts.itSetupItems}</Tile>
            </div>
            <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
              {picked.length === 0 ? (
                <div className="tx-subtle" style={{ padding: 16, fontSize: 12.5 }}>
                  {STR.access.onboarding.noPicks}
                </div>
              ) : (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {picked.map((item) => {
                    const mode = onboardingMode(item, hrOnly);
                    return (
                      <li
                        key={item.id}
                        style={{
                          display: 'flex',
                          gap: 10,
                          alignItems: 'center',
                          padding: '9px 14px',
                          borderBottom: '1px solid var(--border)',
                          fontSize: 12.5,
                        }}
                      >
                        <span style={{ minWidth: 0, flex: 1 }}>
                          <b>{item.name}</b>{' '}
                          <span className="tx-subtle">
                            ·{' '}
                            {item.application === null
                              ? STR.access.onboarding.itSetsUp
                              : STR.access.onboarding.suiteGrant}
                          </span>
                        </span>
                        <span className="tx-subtle" style={{ flex: 'none', fontSize: 11.5 }}>
                          {mode === 'direct'
                            ? STR.access.onboarding.givenAtOnce
                            : STR.access.onboarding.request}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <Callout tone="neutral">
              {STR.access.onboarding.passwordNotice(person.name, person.email)}
            </Callout>
          </>
        ) : null}
      </ModalForm>
    </Modal>
  );
}

function StepRail({ current }: { current: Step }) {
  const labels = [
    STR.access.onboarding.stepPerson,
    STR.access.onboarding.stepAccess,
    STR.access.onboarding.stepReview,
  ];
  return (
    <ol
      aria-label={STR.access.onboarding.progress}
      style={{
        listStyle: 'none',
        margin: '0 0 2px',
        padding: 0,
        display: 'grid',
        gridTemplateColumns: 'repeat(3,minmax(0,1fr))',
        gap: 8,
      }}
    >
      {labels.map((label, index) => {
        const number = (index + 1) as Step;
        const done = number < current;
        return (
          <li
            key={label}
            aria-current={number === current ? 'step' : undefined}
            className={number === current ? 'tx-muted' : 'tx-subtle'}
            style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 11.5, fontWeight: 650 }}
          >
            <span
              className={done ? 'c-emerald' : number === current ? 'c-indigo' : 'c-slate'}
              style={{
                width: 23,
                height: 23,
                borderRadius: 999,
                display: 'grid',
                placeItems: 'center',
                border: '1px solid var(--border-strong)',
                fontSize: 10.5,
              }}
            >
              {done ? '✓' : number}
            </span>
            {label}
          </li>
        );
      })}
    </ol>
  );
}

function TextField({
  label,
  type,
  value,
  placeholder,
  onChange,
}: {
  label: string;
  type: 'text' | 'email';
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
}) {
  return (
    <label style={{ display: 'block' }}>
      <span className="tx-muted" style={fieldLabelStyle}>
        {label}
      </span>
      <input
        className="field"
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        style={textFieldStyle}
      />
    </label>
  );
}
